import {
	Color,
	CubeUVReflectionMapping,
	EquirectangularReflectionMapping,
	EquirectangularRefractionMapping,
	PMREMGenerator,
	RepeatWrapping,
	VideoTexture,
} from 'three';

function throttle(callback, delay) {
	let last;
	let timer;

	return function () {
		const context = this;
		const now = +new Date();
		const args = arguments;
		if (last && now < last + delay) {
			clearTimeout(timer);
			timer = setTimeout(function () {
				last = now;
				callback.apply(context, args);
			}, delay);
		} else {
			last = now;
			callback.apply(context, args);
		}
	};
}

function debounce(fn, delay = 1, opts = {}) {
	const thisArg = opts.bind || null;
	const trail = opts.trail !== null ? !!opts.trail : true;

	let pa1, pa2, pa3; // proxy args
	let timeout;
	let needTrail = trail;

	function delayed() {
		timeout = null;
		if (trail) needTrail = true;
		fn.call(thisArg, pa1, pa2, pa3);
	}

	return function (a1, a2, a3) {
		if (timeout) clearTimeout(timeout);

		pa1 = a1;
		pa2 = a2;
		pa3 = a3;

		if (trail && needTrail) {
			needTrail = false;
			fn.call(thisArg, pa1, pa2, pa3);
		}

		timeout = setTimeout(delayed, delay);
	};
}

const cachedEnvMap = new Map();
function computeEnvmap(renderer, texture, refraction = false, forceRecomputed = false) {
	const btoaConfig = window.btoa(JSON.stringify(texture.uuid + refraction));
	if (cachedEnvMap.get(btoaConfig) && !forceRecomputed) {
		return cachedEnvMap.get(btoaConfig);
	}

	texture.mapping = refraction ? EquirectangularRefractionMapping : EquirectangularReflectionMapping;
	const pmremGenerator = new PMREMGenerator(renderer);
	pmremGenerator.compileEquirectangularShader();

	const pmrem = pmremGenerator.fromEquirectangular(texture).texture;

	const envMapCubeUVHeight = !!pmrem && pmrem?.mapping === CubeUVReflectionMapping ? pmrem.image.height : null;
	if (envMapCubeUVHeight === null) return null;
	const maxMip = Math.log2(envMapCubeUVHeight) - 2;
	const texelHeight = 1.0 / envMapCubeUVHeight;
	const texelWidth = 1.0 / (3 * Math.max(Math.pow(2, maxMip), 7 * 16));
	pmrem.userData = {
		...texture.userData,
		...pmrem.userData,
		texelWidth,
		texelHeight,
		maxMip,
	};

	cachedEnvMap.set(btoaConfig, pmrem);

	texture.dispose();
	pmremGenerator.dispose();

	return pmrem;
}

function createVideo(src) {
	const el = document.createElement('video');
	el.src = src;
	el.crossOrigin = 'anonymous';
	el.muted = true;
	el.autoplay = true;
	el.volume = 0;
	el.preload = 'auto';
	el.loop = true;
	el.playsInline = true;

	const canPlay = () => {
		el.play();
		el.removeEventListener('canplay', canPlay);
	};

	el.addEventListener('canplay', canPlay);

	el.setAttribute('playsinline', 'true');
	el.setAttribute('autoplay', 'true');
	el.setAttribute('webkit-playsinline', 'true');
	el.setAttribute('x-webkit-airplay', 'true');
	el.setAttribute('x5-video-player-type', 'h5');
	el.setAttribute('type', 'video/mp4');
	el.setAttribute('loop', 'true');

	const texture = new VideoTexture(el);
	texture.flipY = false;
	texture.wrapS = texture.wrapT = RepeatWrapping;

	return { el, texture };
}

function disposeMesh(mesh) {
	mesh.traverse((child) => {
		if (child.material) {
			child.material.map?.dispose();
			child.material.normalMap?.dispose();
			child.material.alphaMap?.dispose();
			child.material.aoMap?.dispose();
			child.material.specularMap?.dispose();
			child.material.metalnessMap?.dispose();
			child.material.emissiveMap?.dispose();
			child.material.dispose();

			if (child.material.uniforms) {
				Object.values(child.material.uniforms).forEach((u) => {
					if (u.value?.isTexture) u.value.dispose();
				});
			}

			if (child.userData?.uniforms) {
				Object.values(child.userData.uniforms).forEach((u) => {
					if (u.value?.isTexture) u.value.dispose();
				});
			}
		}
		child.geometry?.dispose();
	});
}

function btoaMaterial(config, caller) {
	return window.btoa(
		JSON.stringify(config) +
			!!caller.instanceMatrix +
			caller.isSkinnedMesh +
			(caller.material?.roughnessMap || '') +
			(caller.material?.metalnessMap || '') +
			(caller.material?.normalMap || '') +
			(caller.userData.ao || '') +
			(caller.userData.material || '') +
			(caller.userData.material || '') +
			(caller.userData.materialConfig || ''),
	);
}

function intToFloatDefine(x) {
	const str = '' + x;

	if (str.indexOf('.') >= 0) return str;
	return str + '.';
}

function parseConfig(obj, typeMapping) {
	Object.entries(typeMapping).forEach(([key, type]) => {
		const value = obj[key];

		if (value instanceof type) return;

		// @ts-ignore
		if (Array.isArray(value)) obj[key] = new type().fromArray(value);
		else if (typeof value === 'string' && type === Color) obj[key] = new Color(value);
	});
}

export { btoaMaterial, computeEnvmap, debounce, createVideo, disposeMesh, intToFloatDefine, parseConfig, throttle };
